/**
 * Turning the adoption payload into what the screen shows.
 *
 * Kept apart from the components because every one of these is a decision about meaning — what "no
 * median" means, which unit a panel is counting in — and those are worth testing.
 *
 * @module Pages/BUs/OptInFeatures
 */

/** `0.189` → `18,9%`. */
export const formatRate = (rate: number): string =>
  `${(rate * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export const formatCount = (count: number): string => count.toLocaleString('pt-BR');

/**
 * The median days until someone gives up.
 *
 * `null` (or a missing value) means nobody has reverted yet, which is not the same as zero days.
 */
export const formatMedianDays = (days: number | null | undefined): string => {
  if (days === null || days === undefined) return '—';
  if (days === 1) return '1 dia';

  return `${formatCount(days)} dias`;
};

/** `2026-10-01` → `01/10`, without letting a timezone move the day. */
export const formatSeriesDate = (date: string): string => {
  const [, month, day] = date.split('-');

  if (!month || !day) return date;

  return `${day}/${month}`;
};

/**
 * Short date for the user table, or a dash when the event never happened.
 *
 * Someone who is still on the feature has no `disabledAt`, and a dash says that plainly instead
 * of leaving the cell blank.
 */
export const formatEventDate = (value: string | null | undefined): string => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * The two units side by side: `"388 eventos de 361 pessoas"`.
 *
 * Every panel on the adoption screen draws one unit, and until this existed none of them said
 * which — so a count of three toggles next to a "Voltaram atrás" of 1 read as a contradiction
 * rather than as one person who flipped it three times.
 *
 * The shape is kept even when the two numbers are equal. A label that reads
 * `"128 eventos de 128 pessoas"` is mildly redundant; one that changes shape whenever they happen
 * to match is harder to scan, and hides the very comparison it is there to make.
 */
export const formatUnits = (
  units: { events: number; people: number },
  singular: string,
  plural: string,
): string => {
  const events = units.events === 1 ? `1 ${singular}` : `${formatCount(units.events)} ${plural}`;
  const people = units.people === 1 ? '1 pessoa' : `${formatCount(units.people)} pessoas`;

  return `${events} de ${people}`;
};

/**
 * `"3 ativações e 2 desativações"` — the toggles behind a day's two bars.
 *
 * The bars count people, so without this a day where one person flipped three times is drawn
 * exactly like a quiet one. It is phrased per action rather than as a single total because a lone
 * `"5 eventos"` sits next to two bars reading `1` and `1`, and the eye adds them and demands they
 * make 5. They never will — and the number that answers "then where did 5 come from" is the split.
 */
export const formatDayToggles = (point: {
  enabledEvents: number;
  disabledEvents: number;
}): string => {
  const on =
    point.enabledEvents === 1 ? '1 ativação' : `${formatCount(point.enabledEvents)} ativações`;
  const off =
    point.disabledEvents === 1
      ? '1 desativação'
      : `${formatCount(point.disabledEvents)} desativações`;

  return `${on} e ${off}`;
};
